import Dropdown from '../../../../../../components/general/Dropdown.tsx'
import DateField from '../../../../../../components/general/DateField.tsx'
import BankAccountSelector from '../../../../../../components/specific/BankAccountSelector.tsx'
import InfoTooltip from '../../../../../../components/general/InfoTooltip.tsx'
import {cn} from '../../../../../../lib/utils.ts'
import {fieldLabelClasses} from '../../../../../../components/general/fieldStyles.ts'
import {CustomerBankAccount, CustomerBankAccountWithIban} from '../../../../../../api/customerBankAccounts/CustomerBankAccount.ts'
import {paymentTermOptions} from '../types.ts'
import {yearFromISODate} from '../utils/invoiceLines.ts'
import type {PaymentTerm} from "../../../../../../generated/enums.ts";

type InvoiceDetailsSectionProps = {
    invoiceDate: string
    onInvoiceDateChange: (date: string) => void
    paymentTerm: PaymentTerm
    onPaymentTermChange: (term: PaymentTerm) => void
    bankAccounts: CustomerBankAccountWithIban[]
    selectedBankAccount: CustomerBankAccountWithIban | null
    onSelectBankAccount: (account: CustomerBankAccount | null) => void
    fiscalYear: number | null
    onFiscalYearChange: (year: number) => void
    invoiceDateError?: string
    bankAccountError?: string
}

export function InvoiceDetailsSection({
    invoiceDate,
    onInvoiceDateChange,
    paymentTerm,
    onPaymentTermChange,
    bankAccounts,
    selectedBankAccount,
    onSelectBankAccount,
    fiscalYear,
    onFiscalYearChange,
    invoiceDateError,
    bankAccountError,
}: InvoiceDetailsSectionProps) {
    const invoiceYear = yearFromISODate(invoiceDate)
    const selectedFiscalYear = fiscalYear ?? invoiceYear
    const fiscalYearOptions = [invoiceYear - 1, invoiceYear, invoiceYear + 1].map(year => ({
        value: year,
        label: String(year),
    }))

    return (
        <section>
            <h2 className="text-xl font-bold">Datum &amp; betaling</h2>
            <p className="text-xs text-gray-600 mt-1 mb-4">
                De factuurdatum bepaalt de vervaldatum. De betaling ontvang je op de gekozen bankrekening.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <div className="flex items-center mb-1">
                        <label className={fieldLabelClasses}>Factuurdatum</label>
                        <InfoTooltip className="ml-1" text="De datum die op de factuur komt te staan" position="right"/>
                    </div>
                    <DateField
                        id="invoiceDate-input"
                        label=""
                        className={invoiceDateError ? 'ring-2 ring-red-500' : ''}
                        value={invoiceDate}
                        onChange={onInvoiceDateChange}
                    />
                    {invoiceDateError && (
                        <p className="mt-1 text-xs text-red-600">{invoiceDateError}</p>
                    )}
                </div>
                <div id="paymentTerm-container" className="flex flex-col">
                    <label className={cn(fieldLabelClasses, 'mb-1')}>Betaaltermijn</label>
                    <Dropdown
                        selected={paymentTermOptions.find(opt => opt.value === paymentTerm) ?? paymentTermOptions[2]}
                        options={paymentTermOptions}
                        onUpdateFunc={(value) => onPaymentTermChange(value as PaymentTerm)}
                    />
                </div>
                <div id="bank-account-selector" tabIndex={-1} className="flex flex-col">
                    <label className={cn(fieldLabelClasses, 'mb-1')}>Bankrekening voor betaling</label>
                    {bankAccounts.length > 0 ? (
                        <BankAccountSelector
                            variant="field"
                            selectedAccountId={selectedBankAccount?.id}
                            bankAccounts={bankAccounts}
                            onSelect={onSelectBankAccount}
                        />
                    ) : (
                        <span className="text-sm text-gray-500 h-9 flex items-center">Geen bankrekening beschikbaar</span>
                    )}
                    {bankAccountError && (
                        <p className="mt-1 text-xs text-red-600">{bankAccountError}</p>
                    )}
                </div>
                <div className="flex flex-col">
                    <div className="flex items-center mb-1">
                        <label className={fieldLabelClasses}>Boekjaar</label>
                        <InfoTooltip
                            className="ml-1"
                            text="Het boekjaar bepaalt in welke periode de factuur wordt geboekt. Het factuurnummer volgt altijd de factuurdatum."
                            position="right"
                        />
                    </div>
                    <Dropdown
                        selected={fiscalYearOptions.find(opt => opt.value === selectedFiscalYear) ?? fiscalYearOptions[1]}
                        options={fiscalYearOptions}
                        onUpdateFunc={(value) => onFiscalYearChange(Number(value))}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        {selectedFiscalYear === invoiceYear
                            ? 'Gelijk aan de factuurdatum.'
                            : `Wijkt af van de factuurdatum (${invoiceYear}).`}
                    </p>
                </div>
            </div>
        </section>
    )
}
